import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useCourseModeration } from '@/features/moderation/useCourseModeration'
import { publishButtonState } from '@/features/moderation/lib/publishButtonState'
import { useGetEditorCourseQuery } from '@/features/editor/coursesApi'
import {
  courseFieldIssues,
  lessonFieldIssues,
  requiredLangs,
} from '@/features/editor/lib/publishIssues'
import { useHasStartedTranslationRun } from '../studio/useBlocksInRun'
import { PublishIssuesTooltip } from './PublishIssuesTooltip'
import { setDialogOpen } from '../../editorSlice'
import {
  PUBLISH_BUTTON_VIEW,
  publishButtonIcon,
  publishButtonLabel,
  publishButtonTitle,
  checksPublishIssues,
} from './publishButtonView'
import { useUiLang } from '@/i18n'

export const PublishButton = ({
  courseId,
  isCourseAuthor,
}: {
  courseId: number
  isCourseAuthor: boolean
}) => {
  // Подпись и подсказка кнопки читают язык в момент вызова — подписываемся на его смену.
  useUiLang()
  const dispatch = useAppDispatch()
  const saveStatus = useAppSelector((s) => s.editor.saveStatus)
  const isOpen = useAppSelector((s) => s.editor.dialogs.publishOpen)
  const { state: moderation, isError } = useCourseModeration(courseId)
  const state = publishButtonState(moderation, isError)
  const view = PUBLISH_BUTTON_VIEW.get(state) ?? PUBLISH_BUTTON_VIEW.get('unknown')
  // Подсказка и гашение — одна функция: у погашенной кнопки всегда есть причина, у живой — нет.
  const title = publishButtonTitle(state, saveStatus, isCourseAuthor)
  const { data: course } = useGetEditorCourseQuery(courseId)
  // Первая публикация и обновление: облачко списком называет всё, что мешает
  // публикации. Незаполненные поля курса гасят кнопку сразу; экраны уроков
  // проверяются при наведении (см. `PublishIssuesTooltip`).
  const startedRun = useHasStartedTranslationRun(courseId)
  // Публикация неопубликованного курса — первая и повторная (после отказа или
  // сбоя проверки). У обновления живого курса список и гашение живут на кнопке
  // «Обновить» внутри окна (`DraftChangesSection`), а шапка лишь открывает окно.
  const checks = checksPublishIssues(state, moderation)
  const missing = checks ? courseFieldIssues(course, startedRun) : []
  const lessonMissing = checks ? lessonFieldIssues(course, startedRun) : []
  const fieldsMissing = missing.length > 0 || lessonMissing.length > 0
  // Блоки уроков проверяются сразу (`eager`), а не по наведению, — иначе кнопка
  // не знала бы, гаснуть ли ей из-за пустой картинки или квиза без вопроса.
  const [blocksBlocked, setBlocksBlocked] = useState(false)
  const blockedByIssues = checks && (fieldsMissing || blocksBlocked)
  if (!view) return null
  const { className, spin } = view
  const label = publishButtonLabel(state, moderation, view.label)
  const Icon = publishButtonIcon(state, moderation, view.Icon)

  const button = (
    <button
      // Кнопка — переключатель облачка публикации (оно не модальное, см. `PublishDialog`).
      data-publish-trigger
      aria-expanded={isOpen}
      onClick={() => dispatch(setDialogOpen({ dialog: 'publishOpen', open: !isOpen }))}
      // Незаполненное (поля и блоки) гасит кнопку и у первой, и у повторной
      // публикации (решение заказчика): причины отказа видны в списке облачка.
      disabled={title !== undefined || blockedByIssues}
      title={fieldsMissing || blocksBlocked ? undefined : title}
      className={className}
    >
      <Icon className={spin ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
      <span className="hidden lg:inline">{label}</span>
    </button>
  )
  if (!checks || !course) return button
  return (
    <PublishIssuesTooltip
      eager
      suppressed={isOpen}
      onBlocked={setBlocksBlocked}
      lessons={course.lessons ?? []}
      fieldIssues={missing}
      lessonIssues={lessonMissing}
      langs={requiredLangs(course, startedRun)}
    >
      {button}
    </PublishIssuesTooltip>
  )
}
